import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AdminRoute from "./AdminRoute";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, resetCart } from "../store/slices/UserDetailsSlice";
import { setLoader } from "../store/slices/UserJourneyDataSlice";
import { setApiBaseUrl } from "../store/slices/apiBaseUrlSlice";
import { setBaseUrl } from "../utils/setBaseUrl";
import { setUserData, normalizePhoneNumber } from "../utils/userUtils";

// Lazy-load all page-level components for code splitting
// This means only the code needed for the current page is downloaded
const HomePage            = lazy(() => import("../pages/HomePage"));
const AdminSignupLogin    = lazy(() => import("../pages/AdminSignupLogin"));
const NotFound            = lazy(() => import("../pages/NotFound"));
const CustomerCare        = lazy(() => import("../components/User/UserPages/CustomerCare"));
const AdminLayout         = lazy(() => import("../components/Admin/AdminLayout"));
const UserLayout          = lazy(() => import("../components/User/UserLayout"));
const UserCart            = lazy(() => import("../components/User/UserPages/UserCart"));
const UserCategory        = lazy(() => import("../components/User/UserPages/UserCategory"));
const UserSubCategory     = lazy(() => import("../components/User/UserPages/UserSubCategory"));
const UserProduct         = lazy(() => import("../components/User/UserPages/UserProduct"));
const UserProfile         = lazy(() => import("../components/User/UserPages/UserProfile"));
const UserCheckout        = lazy(() => import("../components/User/UserPages/UserCheckout"));
const UserLogin           = lazy(() => import("../components/User/UserLogin"));
const UserOrders          = lazy(() => import("../components/User/UserOrders"));
const UserSearch          = lazy(() => import("../components/User/UserSearch"));
const UserAllProducts     = lazy(() => import("../components/User/UserPages/UserAllProducts"));
const UserAllCategories   = lazy(() => import("../components/User/UserPages/UserAllCategories"));

const UserCareer          = lazy(() => import("../components/User/UserPages/UserCareer"));
const UserAboutUs         = lazy(() => import("../components/User/UserPages/UserAboutUs"));
const UserFaq             = lazy(() => import("../components/User/UserPages/UserFaq"));
const PolicyPage          = lazy(() => import("../components/User/UserPages/PolicyPage"));
const Wishlist            = lazy(() => import("../components/User/Wishlist"));
const Affiliate           = lazy(() => import("../components/Affiliate"));
const AffiliateDashboard  = lazy(() => import("../components/Affiliate/AffiliateDashboard"));
const AffiliateLogin      = lazy(() => import("../components/Affiliate/AffiliateLogin"));
const BecomeSeller        = lazy(() => import("../components/BecomeSeller"));
const SellerLogin         = lazy(() => import("../components/SellerLogin"));
const ForgotPassword      = lazy(() => import("../components/Auth/ForgotPassword"));
const ResetPassword       = lazy(() => import("../components/Auth/ResetPassword"));
const SellerDashboard     = lazy(() => import("../components/SellerDashboard"));
const SellerOrders        = lazy(() => import("../components/SellerDashboard/SellerOrders"));
const SellerProducts      = lazy(() => import("../components/SellerDashboard/SellerProducts"));
const SellerProductForm   = lazy(() => import("../components/SellerDashboard/SellerProductForm"));
const SellerSettings      = lazy(() => import("../components/SellerDashboard/SellerSettings"));
const Categories          = lazy(() => import("../components/Admin/AdminSections/Categories"));
const SubCategories       = lazy(() => import("../components/Admin/AdminSections/SubCategories"));
const Orders              = lazy(() => import("../components/Admin/AdminSections/Orders"));
const AddAdvertise        = lazy(() => import("../components/Admin/AdminSections/AddAdvertise"));
const Products            = lazy(() => import("../components/Admin/AdminSections/Products"));
const ChangeWebInfo       = lazy(() => import("../components/Admin/AdminSections/ChangeWebInfo"));
const OrderDashboard      = lazy(() => import("../components/Admin/AdminSections/OrderDashboard"));
const AbandonedCarts      = lazy(() => import("../components/Admin/AdminSections/AbandonedCarts"));
const StoreSettings      = lazy(() => import("../components/Admin/AdminSections/StoreSettings"));

// Branded page-transition spinner matching Quirckart maroon palette
const PageLoader = () => (
  <div style={{
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fdfbf7',
    gap: '12px'
  }}>
    <div style={{
      width: '36px',
      height: '36px',
      border: '3px solid #e8ddd5',
      borderTop: '3px solid #5a1818',
      borderRadius: '50%',
      animation: 'qk-spin 0.75s linear infinite'
    }} />
    <style>{`@keyframes qk-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);


const App = () => {
  const dispatch = useDispatch();
  const userAccessToken = localStorage.getItem('userAccessToken')
  const userSession = JSON.parse(localStorage.getItem('userSession'))
  const { baseUrl } = useSelector((state) => state.apiBaseUrl)

  const getUserData = async () => {
    console.log("Starting getUserData function");
    dispatch(setLoader(true));

    try {
      // Step 1: Create or fetch user data
      console.log("Sending customer search/create request...");
      
      // Normalize phone number to ensure consistency
      const normalizedPhone = normalizePhoneNumber(userSession?.user?.phoneNumber);
      
      const response = await axios.post(`${baseUrl}api/customer-search-create/`, {
        username: normalizedPhone,
        password: normalizedPhone,
        communication_consent: localStorage.getItem('communication_consent_decision') === 'agreed',
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: false  // <--- prevent sending cookies
      });
      const createData = response?.data;
      console.log("Customer create response:", createData);

      if (!createData?.customer_id) {
        throw new Error("Customer ID not found");
      }

      // Step 2: Fetch cart items from the server
      console.log("Fetching customer data...");
      const customerResponse = await axios.get(`${baseUrl}api/customers/${createData?.customer_id}/`);
      const cartItemsDetail = customerResponse?.data?.cart_items_detail;
      const serverCartItems = Array.isArray(cartItemsDetail) 
        ? cartItemsDetail.map((obj) => ({
        ...obj.item,
        quantity: obj.quantity,
          }))
        : [];
      console.log("Server cart items:", serverCartItems);

      // Step 3: Merge with local storage cart
      console.log("Merging server cart with local cart...");
      const localCartStr = localStorage.getItem("cartItems");
      const localCartItems = localCartStr ? JSON.parse(localCartStr) : [];
      
      const mergedMap = new Map();
      serverCartItems.forEach(item => mergedMap.set(item.id, item));
      localCartItems.forEach(item => {
        if (mergedMap.has(item.id)) {
          const existing = mergedMap.get(item.id);
          mergedMap.set(item.id, { ...existing, quantity: Math.max(existing.quantity, item.quantity) });
        } else {
          mergedMap.set(item.id, item);
        }
      });
      const finalCartItems = Array.from(mergedMap.values());

      // Sync merged cart back to server if local had items
      if (localCartItems.length > 0) {
        try {
          const alteredCartItems = finalCartItems.map(({ id, name, quantity }) => ({ item: id, item_name: name, quantity }));
          await axios.patch(`${baseUrl}api/customers/${createData.customer_id}/`, { "cart_items": alteredCartItems });
        } catch (err) {
          console.error("Failed to sync merged cart:", err);
        }
      }

      // Step 4: Update storage and Redux with server cart items
      console.log("Updating storage and Redux with server cart items...");
      sessionStorage.setItem("cartItems", JSON.stringify(finalCartItems));
      localStorage.setItem("cartItems", JSON.stringify(finalCartItems));
      dispatch(resetCart(finalCartItems));
      console.log("Cart successfully updated.");
    } catch (error) {
      console.error("Error in getUserData:", error.response?.data || error.message);
    } finally {
      dispatch(setLoader(false));
      console.log("Finished getUserData function");
    }
  };

  useEffect(() => {
    if (userAccessToken) {
      getUserData();
    }
  }, [userAccessToken])

  useEffect(() => {
    const cartLocalItems = localStorage.getItem('cartItems')
    if (!userAccessToken && cartLocalItems) {
      dispatch(addToCart(JSON.parse(cartLocalItems)))

      // dispatch(resetCart(JSON.parse(cartLocalItems)));
    }
  }, [])


  return (
    <Suspense fallback={<PageLoader />}>
    <Router>
      <Routes>
        {/* User Routes wrapped with UserLayout */}
        <Route element={<UserLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="customer-care" element={<CustomerCare />} />
          <Route path="contact-us" element={<CustomerCare />} />
          <Route path="contact" element={<CustomerCare />} />
          <Route path="terms-and-conditions" element={<PolicyPage defaultType="terms" />} />
          <Route path="terms" element={<PolicyPage defaultType="terms" />} />
          <Route path="privacy-policy" element={<PolicyPage defaultType="privacy" />} />
          <Route path="privacy" element={<PolicyPage defaultType="privacy" />} />
          <Route path="shipping-policy" element={<PolicyPage defaultType="shipping" />} />
          <Route path="cancellation-policy" element={<PolicyPage defaultType="cancellation" />} />
          <Route path="career" element={<UserCareer />} />
          <Route path="about-us" element={<UserAboutUs />} />
          <Route path="faq" element={<UserFaq />} />
          <Route path="login" element={<UserLogin />} />
          <Route path="affiliate-registration" element={<Affiliate />} />
          <Route path="affiliate" element={<AffiliateLogin />} />
          <Route path="affiliate-dashboard" element={<AffiliateDashboard />} />
          <Route path="cart" element={<UserCart />} />
          <Route path="checkout" element={<UserCheckout />} />
          <Route path="orders" element={<UserOrders />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="/category/:categoryId" element={<UserCategory />} />
          <Route path="/category/:categoryId/subcategory/:subCategoryId" element={<UserSubCategory />} />
          <Route path="category/:categoryId/subcategory/:subCategoryId/product/:productId" element={<UserProduct />} />
          <Route path="/product/:productId" element={<UserProduct />} />
          <Route path="/product/:productId/:affiliateCode" element={<UserProduct />} />
          <Route path="search/:searchQuery" element={<UserSearch />} />
          {/* Seller Routes */}
          <Route path="become-seller" element={<BecomeSeller />} />
          <Route path="seller-login" element={<SellerLogin />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password/:uid/:token" element={<ResetPassword />} />
          <Route path="seller-dashboard" element={<SellerDashboard />} />
          <Route path="seller-orders" element={<SellerOrders />} />
          <Route path="seller-products/add" element={<SellerProductForm />} />
          <Route path="seller-products/edit/:productId" element={<SellerProductForm />} />
          <Route path="seller-products" element={<SellerProducts />} />
          <Route path="seller-settings" element={<SellerSettings />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="all-products" element={<UserAllProducts />} />
          <Route path="all-categories" element={<UserAllCategories />} />

        </Route>
        {/* Admin Routes */}
        <Route path="/admin-dashboard/" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route path="categories" element={<Categories />} />
          <Route path="subCategories" element={<SubCategories />} />
          <Route path="products" element={<Products />} />
          <Route path="orders" element={<Orders />} />
          <Route path="addAdvertisement" element={<AddAdvertise />} />
          <Route path="changeWebInfo" element={<ChangeWebInfo />} />
          <Route path="order-dashboard" element={<OrderDashboard />} />
          <Route path="abandoned-carts" element={<AbandonedCarts />} />
          <Route path="store-settings" element={<StoreSettings />} />
        </Route>
        {/* Public Routes */}
        <Route path="/admin-login" element={<AdminSignupLogin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
    </Suspense>
  );
};

export default App;
